import { HoldingMessage, agentUrl } from '../hooks/useStreamChat';

interface SubscribeToUpdatesParams {
  token: string;
  maxRetries: number;
  retryDelay: number;
  onMessage: (msg: HoldingMessage) => void;
  onError?: (err: unknown) => void;
}

/**
 * Streams holding-message updates using the Fetch API. Returns the underlying
 * AbortController so callers can cancel the request when no longer needed.
 */
export const subscribeToUpdates = ({
  token,
  maxRetries,
  retryDelay,
  onMessage,
  onError = (err) => console.error('Updates stream error', err),
}: SubscribeToUpdatesParams): AbortController => {
  const abortController = new AbortController();

  const run = async () => {
    let attempt = 0;

    const execute = async () => {
      const res = await fetch(`${agentUrl}/agent/chat/updates`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({}),
        signal: abortController.signal,
      });

      if (!res.ok) {
        throw new Error(`Failed to connect to event source: ${res.status}`);
      }

      const reader = res.body?.getReader();
      if (!reader) {
        throw new Error('ReadableStream not supported');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split('\n\n');
        buffer = events.pop() ?? '';

        for (const event of events) {
          if (!event.trim()) continue;

          try {
            const eventData = event.startsWith('data: ')
              ? event.replace('data: ', '')
              : event.trim();

            // Some responses concatenate multiple JSON objects without commas.
            const jsonObjects = eventData.split(/}(?=\s*{)/g);

            jsonObjects.forEach((jsonObj) => {
              const cleaned = jsonObj.endsWith('}') ? jsonObj : jsonObj + '}';
              try {
                const message = JSON.parse(cleaned);
                onMessage(message as HoldingMessage);
              } catch (parseErr) {
                console.warn('Error parsing individual JSON object', parseErr);
              }
            });
          } catch (err) {
            console.warn('Error parsing JSON from event stream:', err);
          }
        }
      }
    };

    while (attempt < maxRetries && !abortController.signal.aborted) {
      try {
        await execute();
        return; // success
      } catch (err) {
        if (abortController.signal.aborted) return;
        attempt += 1;
        if (attempt >= maxRetries) {
          onError(err);
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, retryDelay));
      }
    }
  };

  run();

  return abortController;
};
